import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import employeeApi from "../../api/employeeApi";

// First human-readable message from a DRF error body: {"error": "..."},
// {"detail": "..."}, {"message": "..."} or field errors ({"password": ["..."]}).
export const firstApiErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data === "string") return data || fallback;
  if (Array.isArray(data)) {
    for (const v of data) {
      const m = firstApiErrorMessage(v, null);
      if (m) return m;
    }
    return fallback;
  }
  if (typeof data === "object") {
    for (const key of ["error", "detail", "message"]) {
      const m = firstApiErrorMessage(data[key], null);
      if (m) return m;
    }
    for (const v of Object.values(data)) {
      const m = firstApiErrorMessage(v, null);
      if (m) return m;
    }
  }
  return fallback;
};

export const fetchEmployees = createAsyncThunk(
  "employee/fetchEmployees",
  async ({ page = 1, pageSize = 10, search = "" }, { rejectWithValue }) => {
    try {
      const response = await employeeApi.getAll(page, pageSize, search);
      return response;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.detail || "Failed to load employees"
      );
    }
  }
);

export const fetchCodeSequences = createAsyncThunk(
  "employee/fetchCodeSequences",
  async (_, { rejectWithValue }) => {
    try {
      const response = await employeeApi.getCodeSequences();
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch code sequences");
    }
  }
);

export const createEmployee = createAsyncThunk(
  "employee/createEmployee",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await employeeApi.create(formData);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to create employee");
    }
  }
);

export const updateEmployee = createAsyncThunk(
  "employee/updateEmployee",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await employeeApi.update(id, formData);
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to update employee");
    }
  }
);

export const deleteEmployee = createAsyncThunk(
  "employee/deleteEmployee",
  async (id, { rejectWithValue }) => {
    try {
      await employeeApi.delete(id);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to delete employee");
    }
  }
);

export const changePasswordBySuperadmin = createAsyncThunk(
  "employee/changePasswordBySuperadmin",
  async ({ id, newPassword }, { rejectWithValue }) => {
    try {
      const response = await employeeApi.changePassword(id, newPassword);
      return response;
    } catch (err) {
      // Backend sends {"error": "Password must be at least 8 characters"} etc.
      const msg = firstApiErrorMessage(err.response?.data, "Failed to change password");
      return rejectWithValue(msg);
    }
  }
);

export const exportEmployees = createAsyncThunk(
  "employee/exportEmployees",
  async (_, { rejectWithValue }) => {
    try {
      const blob = await employeeApi.exportCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `employees_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      return rejectWithValue("Export failed");
    }
  }
);

export const importPreview = createAsyncThunk(
  "employee/importPreview",
  async (file, { rejectWithValue }) => {
    try {
      const data = await employeeApi.importPreview(file);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || "Preview failed");
    }
  }
);

export const importConfirm = createAsyncThunk(
  "employee/importConfirm",
  async (preview, { rejectWithValue, dispatch }) => {
    try {
      await employeeApi.importConfirm(preview);
      dispatch(fetchEmployees({ page: 1, pageSize: 10 }));
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || "Import failed");
    }
  }
);

const employeeSlice = createSlice({
  name: "employee",
  initialState: {
    employees: [],
    pagination: { next: null, previous: null, count: 0, page_size: 10 },
    isLoading: false,
    error: null,
    importPreview: null,
    importTotal: null,
    codeSequences: [], 
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setEmployeesForTicket: (state, action) => {
      state.employees = action.payload.data;        // ← Yeh array hai
      state.pagination = action.payload.pagination; // ← Optional, but good to have
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.isLoading = false;
        state.employees = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchCodeSequences.fulfilled, (state, action) => {
        state.codeSequences = action.payload;
      })

      .addCase(createEmployee.fulfilled, (state, action) => {
        state.employees.unshift(action.payload);
        state.pagination.count += 1;
      })

      .addCase(updateEmployee.fulfilled, (state, action) => {
        const idx = state.employees.findIndex(
          (e) => e.id === action.payload.id
        );
        if (idx !== -1) state.employees[idx] = action.payload;
      })

      .addCase(deleteEmployee.fulfilled, (state, action) => {
        state.employees = state.employees.filter(
          (e) => e.id !== action.payload
        );
        state.pagination.count -= 1;
      })

      .addCase(changePasswordBySuperadmin.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(changePasswordBySuperadmin.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(changePasswordBySuperadmin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(exportEmployees.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(importPreview.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(importPreview.fulfilled, (state, action) => {
        state.isLoading = false;
        state.importPreview = action.payload.preview;
        state.importTotal = action.payload.total;
      })
      .addCase(importPreview.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(importConfirm.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(importConfirm.fulfilled, (state) => {
        state.isLoading = false;
        state.importPreview = null;
        state.importTotal = null;
      })
      .addCase(importConfirm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setEmployeesForTicket } = employeeSlice.actions;
export default employeeSlice.reducer;
